// Documents section — visas, ETAs, passports, etc.
// Shows a banner per document with status, expiry, key numbers and option to
// view the original PDF/image saved in the trip data.
import { useState } from "react";
import { ShieldCheck, AlertCircle, Eye, X, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[1]}/${m[2]}/${m[3]}`;
  try {
    const d = new Date(s);
    if (!isNaN(d)) return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {}
  return s;
}

function statusOf(doc) {
  const now = new Date();
  if (doc.validUntil) {
    const m = String(doc.validUntil).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    let exp;
    if (m) exp = new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`);
    else exp = new Date(doc.validUntil);
    if (!isNaN(exp)) {
      const daysToExp = (exp - now) / (1000 * 60 * 60 * 24);
      if (daysToExp < 0) return { tag: "Vencida", color: "hsl(0 70% 50%)", bg: "hsl(0 70% 96%)", icon: AlertCircle };
      if (daysToExp < 30) return { tag: `Vence en ${Math.round(daysToExp)}d`, color: "hsl(35 80% 45%)", bg: "hsl(40 95% 96%)", icon: AlertCircle };
    }
  }
  const norm = (doc.status || "").toLowerCase();
  if (norm.includes("approve") || norm.includes("aprob")) return { tag: "Aprobada", color: "hsl(140 50% 30%)", bg: "hsl(140 40% 95%)", icon: ShieldCheck };
  return { tag: doc.status || "Vigente", color: "hsl(var(--ink))", bg: "hsl(var(--bg-card))", icon: ShieldCheck };
}

export default function DocumentsSection({ documents = [], tripId }) {
  const [previewDoc, setPreviewDoc] = useState(null);

  if (!documents.length) return null;

  // Build the URL to view a document. For embedded files (PDF or image),
  // use the backend endpoint to avoid iOS Safari PWA data-URL blocking.
  // Falls back to fileUrl (remote) when present.
  const docFileUrl = (doc) => {
    if (doc.file || doc.image) {
      if (!tripId) return doc.file || doc.image; // fallback to data URL
      const tok = (() => { try { return localStorage.getItem("session_token"); } catch { return null; } })();
      const tokParam = tok ? `?token=${encodeURIComponent(tok)}` : "";
      return `${API}/api/trips/${tripId}/documents/${doc.id}/file${tokParam}`;
    }
    return doc.fileUrl;
  };

  return (
    <section data-testid="documents-section" className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="section-title flex items-center gap-2">
          <ShieldCheck size={14} /> Documentos de viaje ({documents.length})
        </h3>
      </div>

      <ul className="space-y-3">
        {documents.map((doc) => {
          const st = statusOf(doc);
          const Icon = st.icon;
          return (
            <li
              key={doc.id}
              className="card-paper p-4"
              style={{ borderLeft: `3px solid ${st.color}` }}
              data-testid={`doc-${doc.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: st.bg, color: st.color }}
                >
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                      style={{ background: st.bg, color: st.color }}
                    >
                      {st.tag}
                    </span>
                    {doc.type && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">{doc.type}</span>}
                  </div>
                  <p className="font-semibold leading-tight">{doc.title}</p>
                  {doc.holder && <p className="text-xs text-[hsl(var(--muted))] mt-0.5">{doc.holder}</p>}

                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    {doc.refNumber && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Nº</p>
                        <p className="font-mono font-semibold break-all">{doc.refNumber}</p>
                      </div>
                    )}
                    {doc.passport && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Pasaporte</p>
                        <p className="font-mono font-semibold">{doc.passport}</p>
                      </div>
                    )}
                    {doc.validUntil && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Válido hasta</p>
                        <p className="font-semibold">{fmtDate(doc.validUntil)}</p>
                      </div>
                    )}
                    {doc.issuedBy && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">Emisor</p>
                        <p className="text-xs leading-tight">{doc.issuedBy}</p>
                      </div>
                    )}
                  </div>

                  {doc.notes && (
                    <p className="text-xs text-[hsl(var(--ink-soft))] mt-3 pt-3 border-t border-[hsl(var(--line))] leading-relaxed">
                      📌 {doc.notes}
                    </p>
                  )}

                  {/* Actions */}
                  {(doc.file || doc.fileUrl || doc.image) && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => {
                          const url = docFileUrl(doc);
                          const isPdf = (doc.mime === "application/pdf") ||
                                        (doc.file && doc.file.startsWith("data:application/pdf")) ||
                                        ((doc.fileUrl || "").toLowerCase().endsWith(".pdf"));
                          // PDFs: open in new tab → iOS uses native viewer
                          // Images: show in modal
                          if (isPdf) {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } else {
                            setPreviewDoc({ ...doc, _viewUrl: url });
                          }
                        }}
                        className="flex-1 px-3 py-2 rounded-xl bg-[hsl(var(--ink))] text-white text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-95"
                        data-testid={`doc-view-${doc.id}`}
                      >
                        <Eye size={14} /> Ver documento
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Preview modal */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-3"
          onClick={() => setPreviewDoc(null)}
          data-testid="doc-preview-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center justify-between">
              <p className="font-semibold text-sm truncate pr-2">{previewDoc.title}</p>
              <button onClick={() => setPreviewDoc(null)} className="text-[hsl(var(--muted))]" aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            {previewDoc.image && !previewDoc._viewUrl && (
              <img src={previewDoc.image} alt={previewDoc.title} className="w-full" />
            )}
            {previewDoc._viewUrl && (
              <img src={previewDoc._viewUrl} alt={previewDoc.title} className="w-full" />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
